"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.runAndWaitForEvents3 = void 0;
exports.waitForEvent3 = waitForEvent3;
exports.raceEvents3 = raceEvents3;
exports.runAndRaceEvents3 = runAndRaceEvents3;
const withTimeout_1 = require("./withTimeout");
const Logger_1 = require("./Logger");
const logger = new Logger_1.Logger(module);
const once = (emitter, eventName, predicate = () => true) => {
    let listener;
    const task = new Promise((resolve) => {
        listener = (...eventArgs) => {
            if (predicate(...eventArgs)) {
                resolve(eventArgs);
            }
        };
        emitter.on(eventName, listener);
    });
    const cancel = () => emitter.off(eventName, listener);
    return {
        task,
        cancel
    };
};
/**
 * Wait for an event to be emitted on eventemitter3 within timeout.
 *
 * @param emitter emitter of event
 * @param event event to wait for
 * @param timeout amount of time in milliseconds to wait for
 * @returns {Promise<any[]>} resolves with event arguments if event occurred
 * within timeout else rejects
 */
function waitForEvent3(emitter, eventName, timeout = 5000, predicate = () => true) {
    const { task, cancel } = once(emitter, eventName, predicate);
    return (0, withTimeout_1.withTimeout)(task, timeout, 'waitForEvent3').finally(() => {
        cancel();
    });
}
/**
 * Wait for an event to be emitted on eventemitter3 within timeout.
 *
 * @param emitter emitter of event
 * @param eventNames events to race
 * @param timeout amount of time in milliseconds to wait for
 * @returns {Promise<{eventName: keyof T, eventArgs: any[]}>} resolves with the winning events name and arguments if event occurred
 * within timeout else rejects
 */
function raceEvents3(emitter, eventNames, timeout = 5000) {
    const promises = [];
    eventNames.forEach((eventName) => {
        const item = once(emitter, eventName);
        const wrappedTask = item.task.then((value) => {
            const ret = { winnerName: eventName, winnerArgs: value };
            return ret;
        });
        promises.push({ task: wrappedTask, cancel: item.cancel });
    });
    const cancelAll = () => {
        promises.forEach((promise) => {
            promise.cancel();
        });
    };
    if (timeout !== null) {
        return (0, withTimeout_1.withTimeout)(Promise.race(promises.map((promise) => promise.task)), timeout, 'raceEvents3').finally(() => {
            cancelAll();
        });
    }
    else {
        return Promise.race(promises.map((promise) => promise.task)).finally(() => {
            cancelAll();
        });
    }
}
function runAndRaceEvents3(operations, emitter, eventNames, timeout) {
    const promise = raceEvents3(emitter, eventNames, timeout);
    operations.forEach((op) => {
        try {
            op();
        }
        catch (e) {
            logger.error('runAndRaceEvents3 caught exception ' + e);
        }
    });
    return promise;
}
// internal
const runAndWait = async (operations, waitedEvents, timeout, promiseFn) => {
    const promise = promiseFn(waitedEvents.map(([emitter, event]) => waitForEvent3(emitter, event, timeout)));
    operations.forEach((op) => { op(); });
    return promise;
};
/**
 * Run functions and wait for events to be emitted within timeout. Returns a promise created with Promise.all()
 * and waitForEvent() calls. Calls the functions after creating the promise.
 *
 * @param operations function(s) to call
 * @param waitedEvents event(s) to wait for
 * @param timeout amount of time in milliseconds to wait for
 * @returns {Promise<unknown[]>} resolves with event arguments if event occurred
 * within timeout. Otherwise rejected.
 */
const runAndWaitForEvents3 = async (operations, waitedEvents, timeout = 5000) => {
    return runAndWait(operations, waitedEvents, timeout, Promise.all.bind(Promise));
};
exports.runAndWaitForEvents3 = runAndWaitForEvents3;
//# sourceMappingURL=waitForEvent3.js.map